import { inject, observer } from 'mobx-react';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { v4 } from 'uuid';
import C from '../../colors';
import { IMAGE_ACCEPT, isRawImageFile, isSupportedImageFile } from '../../imageUploadPolicy';

const FileUploader = inject('pictureStore','userStore','snackbarStore')(observer(forwardRef(({
  size, onStartUpload, onUploadProgress, onUploaded, pictureStore, userStore, snackbarStore,
  albumId
}, ref) => {
  const [error, setError] = useState(null);

  const beforeUnload = (e) => { e.returnValue = '上传未完成，确定离开？'; return e.returnValue; };

  const uploadFiles = async (files) => {
    if (userStore.level === 0 && pictureStore.pictures.length > 200) {
      snackbarStore.showSnackbar('免费账户限制 200 张照片', 'error'); return;
    }
    if (!userStore.canUseRaw && files.some(isRawImageFile)) {
      snackbarStore.showSnackbar('RAW 文件解析为高级会员功能，请升级后使用', 'error');
      return;
    }
    onStartUpload(files.length);
    window.addEventListener('beforeunload', beforeUnload);
    let successFileCount = 0, existFileCount = 0, failFileCount = 0;
    const failedFileNames = [], failedFiles = [];
    const reportProgress = () => onUploadProgress?.({
      pictureNum: files.length,
      completedFileCount: successFileCount + existFileCount + failFileCount,
      successFileCount,
      existFileCount,
      failFileCount,
      failedFileNames: [...failedFileNames],
      failedFiles: [...failedFiles],
    });
    try {
      for (const file of files) {
        const uuid = v4();
        const originalFileName = file.name.trim();
        // Detect files already visible (or currently uploading) before adding a
        // progress tile. Inserting and immediately removing that tile makes the
        // photo grid visibly jump for duplicate uploads.
        if (pictureStore.hasPictureNamed(originalFileName)) {
          existFileCount++;
          reportProgress();
          continue;
        }
        let status = 'fail';
        try {
          status = await pictureStore.addPicture(file, uuid, albumId, userStore.maxDimension);
        } catch (uploadError) {
          setError({ message: uploadError.message || '图片上传失败', name: file.name });
        }
        if (status === 'success') successFileCount++;
        else if (status === 'exist') existFileCount++;
        else {
          failFileCount++;
          failedFileNames.push(originalFileName);
          failedFiles.push(file);
        }
        reportProgress();
      }
      onUploaded({ pictureNum: files.length, successFileCount, existFileCount, failFileCount, failedFileNames, failedFiles });
    } finally {
      window.removeEventListener('beforeunload', beforeUnload);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    await uploadFiles(files);
  };

  const handleFolderChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';
    const files = selectedFiles.filter(file => {
      const parts = (file.webkitRelativePath || file.name).split('/').filter(Boolean);
      return parts.length <= 2 && isSupportedImageFile(file);
    });
    const ignoredCount = selectedFiles.length - files.length;
    if (ignoredCount > 0) {
      snackbarStore.showSnackbar(`已忽略 ${ignoredCount} 个子目录或非图片文件`);
    }
    if (!files.length) {
      snackbarStore.showSnackbar('所选文件夹第一层没有可导入的图片', 'error');
      return;
    }
    await uploadFiles(files);
  };

  useImperativeHandle(ref, () => ({ retryFiles: uploadFiles }));

  const isSmall = size === 'small';
  return (
    <div>
      {error && (
        <div data-testid="file-upload-error" style={{ marginBottom: 8, padding: '8px 12px', background: C.dangerBg, borderRadius: 8, fontSize: 12, color: C.dangerFg }}>
          {error.message} — {error.name}
          <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: C.dangerFg }}>×</button>
        </div>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isSmall ? '7px 14px' : '9px 18px',
          borderRadius: 8, border: 'none',
          background: C.ink, color: '#fff',
          fontFamily: C.sans, fontSize: isSmall ? 12 : 13, fontWeight: 500,
          cursor: 'pointer', boxShadow: '0 1px 0 rgba(0,0,0,.2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          上传照片
          <input data-testid="photo-upload-input" type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={handleFileChange}/>
        </label>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isSmall ? '6px 13px' : '8px 17px',
          borderRadius: 8, border: `1px solid ${C.hair2}`,
          background: '#fff', color: C.ink2,
          fontFamily: C.sans, fontSize: isSmall ? 12 : 13, fontWeight: 500,
          cursor: 'pointer',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7.5h7l2 2h9v9.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M3 7.5V5a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2.5"/>
          </svg>
          导入文件夹
          <input data-testid="folder-upload-input" type="file" multiple webkitdirectory="" directory="" hidden onChange={handleFolderChange}/>
        </label>
      </div>
    </div>
  );
})));
export default FileUploader;
